import { useQuery } from '@apollo/client'
import { Person, Guest_Card, Property } from 'src/@types/graphql'
import { formatPhoneNumber } from 'src/helpers'
import { GET_PERSON } from '../data/queries'
import { CountryCode } from 'libphonenumber-js'
import { useEffect, useMemo } from 'react'
import { CONTACT_CONVERSATION_SUBSCRIPTION } from '../data/subscription'
import useOccupancyToUse from 'src/pages/Inbox/components/InboxBody/components/hooks/useOccupancyToUse'

const usePersonInfo = (person_id: string | number | undefined) => {
  const { data, loading, error, refetch, subscribeToMore } = useQuery(
    GET_PERSON,
    {
      variables: { id: Number(person_id) },
      fetchPolicy: 'cache-first',
      nextFetchPolicy: 'cache-first',
      notifyOnNetworkStatusChange: false,
    },
  )

  const person: Person = data?.person || {}

  const properties = useMemo(
    () =>
      (person?.person_properties?.map((card) => card?.property) ??
        []) as Property[],
    [person?.person_properties],
  )

  const primaryPhone = useMemo(
    () => person?.phones?.find((phone) => phone.is_default)?.phone_number ?? '',
    [person?.phones],
  )

  const primaryEmail = useMemo(
    () => person?.emails?.find((email) => email.is_default)?.address ?? '',
    [person?.emails],
  )

  const guestCards: Guest_Card[] = useMemo(
    () =>
      (person?.person_guest_cards?.map((pgc) => pgc.guest_card) ??
        []) as Guest_Card[],
    [person?.person_guest_cards],
  )

  const applications = useMemo(
    () =>
      person?.person_applications?.map(
        (person_application) => person_application.application,
      ) || [],
    [person?.person_applications],
  )

  const type = useMemo(() => person?.type?.toLowerCase(), [person?.type])

  const secondaryPhones = useMemo(
    () =>
      person?.phones
        ?.filter((phone) => !phone.is_default)
        ?.map((phone) => {
          if (phone?.phone_number) {
            return formatPhoneNumber(
              phone?.phone_number,
              phone?.country_code as CountryCode,
            )
          }
        }),
    [person?.phones],
  )

  const secondaryEmails = useMemo(
    () =>
      person?.emails
        ?.filter((email) => !email.is_default)
        ?.map((email) => email?.address),
    [person?.emails],
  )

  const { occupancyToUse } = useOccupancyToUse(person)

  useEffect(() => {
    if (person?.conversation?.id) {
      subscribeToMore({
        document: CONTACT_CONVERSATION_SUBSCRIPTION,
        variables: {
          conversation_id: person.conversation.id,
        },
        updateQuery: (prev, { subscriptionData }) => {
          return {
            person: {
              ...prev.person,
              conversation: {
                ...prev.person.conversation,
                ...subscriptionData?.data?.conversationChanged,
              },
            },
          }
        },
      })
    }
  }, [person?.conversation?.id, subscribeToMore])

  return {
    data,
    loading,
    error,
    person,
    properties,
    primaryPhone,
    primaryEmail,
    guestCards,
    applications,
    type,
    secondaryPhones,
    secondaryEmails,
    occupancy: occupancyToUse,
    refetch,
  }
}

export default usePersonInfo
